import { isNothing } from "../../lib/maybe";
/**
 * Comment highlights for the ProseMirror editor (#251). Commented text
 * carries a `comment` mark; this plugin adds the `zw-comment-text` decoration
 * (amber, clickable, `data-comment-open` on the open thread) that the shell's
 * click / hover handling and `styles.css` use. Point comments
 * (`comment_anchor`) highlight the sentence after the anchor.
 */

import type { Node as PMNode } from "prosemirror-model";
import { Plugin, PluginKey, type EditorState } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import {
  zwDocCommentRanges,
  type ZwDocCommentRange,
} from "../../lib/zwdoc/comments";
import { resolveCommentWrapRange } from "../sentenceBounds";

/** One char per position, so offsets in a block's text map 1:1 onto it. */
const LEAF_PLACEHOLDER = "￼";

export type CommentView = {
  /** Off in Focus mode and where the surface forbids comments. */
  enabled: boolean;
  openId: string | null;
  /**
   * Only comments with a thread are highlighted, so a stray mark (pasted from
   * another document, say) never looks like a comment that won't open.
   */
  hasThread: (id: string) => boolean;
};

type CommentState = {
  ranges: readonly ZwDocCommentRange[];
  decorations: DecorationSet;
};

const commentsKey = new PluginKey<CommentState>("zwComments");

/** Sentence (or quotation) around `pos` inside its textblock. */
export function sentenceRangeAt(
  doc: PMNode,
  pos: number,
): { from: number; to: number } | null {
  const $pos = doc.resolve(pos);
  if ($pos.parent.isTextblock === false) return null;
  const start = $pos.start();
  const text = $pos.parent.textBetween(
    0,
    $pos.parent.content.size,
    undefined,
    LEAF_PLACEHOLDER,
  );
  const range = resolveCommentWrapRange(text, $pos.parentOffset);
  if (isNothing(range)) return null;
  return { from: start + range.value.from, to: start + range.value.to };
}

function highlightRange(
  doc: PMNode,
  range: ZwDocCommentRange,
): { from: number; to: number } | null {
  if (range.point === false) return range;
  return sentenceRangeAt(doc, range.to);
}

function computeComments(state: EditorState, view: CommentView): CommentState {
  const ranges = zwDocCommentRanges(state.doc);
  if (view.enabled === false || ranges.length === 0) {
    return { ranges, decorations: DecorationSet.empty };
  }
  const decorations: Decoration[] = [];
  for (const range of ranges) {
    if (view.hasThread(range.id) === false) continue;
    const highlight = highlightRange(state.doc, range);
    if (highlight === null) continue;
    const attrs: Record<string, string> = {
      class: "zw-comment-text",
      "data-comment-id": range.id,
    };
    if (view.openId === range.id) attrs["data-comment-open"] = "";
    decorations.push(Decoration.inline(highlight.from, highlight.to, attrs));
  }
  return {
    ranges,
    decorations: DecorationSet.create(state.doc, decorations),
  };
}

/** Highlights per `view()`; `refreshComments` re-reads it. */
export function commentsPlugin(view: () => CommentView): Plugin<CommentState> {
  return new Plugin<CommentState>({
    key: commentsKey,
    state: {
      init: (_config, state) => computeComments(state, view()),
      apply: (tr, value, _old, state) =>
        tr.docChanged || tr.getMeta(commentsKey) === true
          ? computeComments(state, view())
          : value,
    },
    props: {
      decorations: (state) => commentsKey.getState(state)?.decorations ?? null,
      attributes: () =>
        view().enabled ? {} : { class: "zw-prose-comments-off" },
    },
  });
}

/** Meta for a transaction that should re-read the comment view. */
export const refreshCommentsMeta = [commentsKey, true] as const;

export function commentRangesOf(
  state: EditorState,
): readonly ZwDocCommentRange[] {
  return commentsKey.getState(state)?.ranges ?? zwDocCommentRanges(state.doc);
}

/** The comment whose text holds `pos` (innermost / latest-starting wins). */
export function commentAt(state: EditorState, pos: number): string | null {
  let found: ZwDocCommentRange | null = null;
  for (const range of commentRangesOf(state)) {
    if (range.point) continue;
    if (pos >= range.from && pos <= range.to) {
      if (found === null || range.from >= found.from) found = range;
    }
  }
  return found?.id ?? null;
}
